/**
 * StatusBadge — the "is the campus OK, and what needs me?" answer, shown in
 * the TopBar status slot. Always text + a shape glyph, so the level never
 * rides on colour alone (WCAG 1.4.1). Severity colours (brick / grey / the
 * one green) are used here because this IS alert severity — tier honesty
 * stays grey elsewhere.
 */
// "unknown" = we cannot currently say (alerts still loading, or the engine is
// unreachable). Never render ALL CLEAR in that state: a false all-clear is the
// most dangerous thing this badge could say.
export type StatusLevel = "critical" | "warning" | "watch" | "clear" | "unknown";

const GLYPH: Record<StatusLevel, string> = {
  critical: "■",
  warning: "▲",
  watch: "●",
  clear: "✓",
  unknown: "?",
};

interface Props {
  level: StatusLevel;
  /** Items needing attention; ignored for "clear". */
  count?: number;
  /** Override the visible text (defaults to ATTENTION · N / ALL CLEAR). */
  label?: string;
  /** Plain-language detail for the tooltip and screen readers. */
  detail?: string;
  onClick?: () => void;
}

export function statusText(level: StatusLevel, count = 0): string {
  if (level === "unknown") return "CHECKING…";
  return level === "clear" ? "ALL CLEAR" : `ATTENTION · ${count}`;
}

export function StatusBadge({ level, count = 0, label, detail, onClick }: Props) {
  const text = label ?? statusText(level, count);
  const a11y = detail ? `${text} — ${detail}` : text;
  const className = `status-badge status-badge--${level} mono`;
  const body = (
    <>
      <span className="status-badge-glyph" aria-hidden>{GLYPH[level]}</span>
      <span>{text}</span>
    </>
  );
  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick} title={detail} aria-label={a11y}>
        {body}
      </button>
    );
  }
  return (
    <span className={className} role="status" title={detail} aria-label={a11y}>
      {body}
    </span>
  );
}
